import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/authStore';
import { getMe, registerUser, loginEmail, getGoogleOAuthConfig } from '../lib/api';
import { Loader2, User, Phone, Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, setUser } = useAuthStore();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [showPassword, setShowPassword] = useState(false);
  const [useCustomGoogleOAuth, setUseCustomGoogleOAuth] = useState(false);

  // Form fields
  const [name, setName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (isAuthenticated) { navigate('/', { replace: true }); return; }
    const checkExisting = async () => {
      try { const res = await getMe(); setUser(res.data); navigate('/', { replace: true }); } catch(e) {}
    };
    checkExisting();
    // Check if custom Google OAuth is available
    const checkGoogleOAuth = async () => {
      try {
        const res = await getGoogleOAuthConfig();
        setUseCustomGoogleOAuth(res.data.enabled === true);
      } catch(e) {}
    };
    checkGoogleOAuth();
  }, [isAuthenticated, navigate, setUser]);

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    if (useCustomGoogleOAuth) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      // Custom Google OAuth — goes directly to Google "Escolha uma conta" (no intermediate page)
      window.location.href = window.location.origin + '/api/auth/google/login';
    } else {
      // Emergent auth fallback (preview environment)
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + '/';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const formatWhatsapp = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (mode === 'register') {
        const res = await registerUser({ name, whatsapp, email, password });
        setUser(res.data);
        toast.success('Conta criada com sucesso!');
      } else {
        const res = await loginEmail({ email, password });
        setUser(res.data);
        toast.success('Login realizado!');
      }
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Erro ao processar. Tente novamente.';
      toast.error(msg);
    }
    setFormLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center px-4 relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />

      {/* Red accent lines */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-[400px] py-8">

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="bg-zinc-900 rounded-full p-3 inline-block mb-4 shadow-2xl shadow-red-600/10 border border-zinc-800">
            <img
              src="/logo.png"
              alt="MotoVeloz"
              className="w-20 h-20 object-contain"
              data-testid="login-logo"
            />
          </div>
          <h1 className="text-center" data-testid="login-brand">
            <span className="text-white text-4xl font-bold tracking-tight block" style={{ fontFamily: 'Teko, sans-serif' }}>
              MOTO<span className="text-red-600">VELOZ</span>
            </span>
          </h1>
          <div className="w-12 h-[2px] bg-red-600 mt-2" />
        </div>

        {/* Card */}
        <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-2xl p-6 shadow-2xl shadow-black/50">

          {/* Headline */}
          <div className="text-center mb-6">
            <h2 className="text-white text-lg font-bold mb-1" data-testid="login-title">
              {mode === 'register' ? 'Crie sua conta' : 'Acesse seu catálogo'}
            </h2>
            <p className="text-zinc-500 text-sm">
              {mode === 'register' ? 'Cadastre-se para favoritar e acompanhar pedidos.' : 'Encontre peças com rapidez e precisão.'}
            </p>
          </div>

          {/* Google Button */}
          <button
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 py-3 px-6 rounded-xl font-bold text-white text-[15px] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            style={{ backgroundColor: googleLoading ? '#991B1B' : '#CC0000' }}
            onMouseEnter={(e) => { if (!googleLoading) e.target.style.backgroundColor = '#FF1A1A'; }}
            onMouseLeave={(e) => { if (!googleLoading) e.target.style.backgroundColor = '#CC0000'; }}
          >
            {googleLoading ? (
              <><Loader2 className="h-5 w-5 animate-spin" /> Entrando...</>
            ) : (
              <>
                <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="white"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="white"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="white"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="white"/>
                </svg>
                Continuar com Google
              </>
            )}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 my-5">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-zinc-600 text-xs uppercase tracking-widest">
              {mode === 'register' ? 'ou cadastre com email' : 'ou entre com email'}
            </span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="auth-form">

            {mode === 'register' && (
              <>
                {/* Nome */}
                <div>
                  <label className="text-white text-xs font-bold block mb-1.5">Nome completo</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Seu nome"
                      required
                      className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg py-2.5 pl-10 pr-4 text-white text-sm placeholder-zinc-600 focus:border-red-600 focus:outline-none transition-colors"
                      data-testid="register-name"
                    />
                  </div>
                </div>

                {/* WhatsApp */}
                <div>
                  <label className="text-white text-xs font-bold block mb-1.5">WhatsApp</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                    <input
                      type="tel"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(formatWhatsapp(e.target.value))}
                      placeholder="(00) 00000-0000"
                      className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg py-2.5 pl-10 pr-4 text-white text-sm placeholder-zinc-600 focus:border-red-600 focus:outline-none transition-colors"
                      data-testid="register-whatsapp"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Email */}
            <div>
              <label className="text-white text-xs font-bold block mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg py-2.5 pl-10 pr-4 text-white text-sm placeholder-zinc-600 focus:border-red-600 focus:outline-none transition-colors"
                  data-testid="auth-email"
                />
              </div>
            </div>

            {/* Senha */}
            <div>
              <label className="text-white text-xs font-bold block mb-1.5">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? 'Mínimo 6 caracteres' : 'Sua senha'}
                  required
                  minLength={mode === 'register' ? 6 : 1}
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg py-2.5 pl-10 pr-10 text-white text-sm placeholder-zinc-600 focus:border-red-600 focus:outline-none transition-colors"
                  data-testid="auth-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={formLoading}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white text-sm transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed bg-red-600 hover:bg-red-700 mt-4"
              data-testid="auth-submit-btn"
            >
              {formLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> {mode === 'register' ? 'Criando conta...' : 'Entrando...'}</>
              ) : (
                <>{mode === 'register' ? 'Criar conta' : 'Entrar'} <ArrowRight className="h-4 w-4" /></>
              )}
            </button>
          </form>

          {/* Toggle mode */}
          <p className="text-center text-zinc-500 text-sm mt-5" data-testid="auth-toggle">
            {mode === 'register' ? (
              <>Já tem uma conta? <button onClick={() => { setMode('login'); setName(''); setWhatsapp(''); }} className="text-red-500 hover:text-red-400 font-bold">Entrar</button></>
            ) : (
              <>Não tem conta? <button onClick={() => setMode('register')} className="text-red-500 hover:text-red-400 font-bold">Criar conta</button></>
            )}
          </p>

          {/* Footer terms */}
          <p className="text-center text-zinc-600 text-[11px] mt-5 leading-relaxed">
            Ao continuar, você concorda com nossos{' '}
            <a href="/politica-de-privacidade" className="text-red-500 cursor-pointer hover:text-red-400">Termos de Uso</a>
            {' '}e{' '}
            <a href="/politica-de-privacidade" className="text-red-500 cursor-pointer hover:text-red-400">Política de Privacidade</a>.
          </p>
        </div>

        {/* Bottom branding */}
        <p className="text-center text-zinc-700 text-[10px] mt-6 uppercase tracking-[0.3em]">
          Sua Jornada. Nossa Peça.
        </p>
      </div>
    </div>
  );
}
